import React, { useState, useRef, useEffect } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import Avatar from "@/components/ui/avatar";
import Chip from "@/components/ui/chip";
import ProgressBar from "@/components/ui/progress-bar";
import Button from "@/components/ui/button";
import {
  Eye as EyeIcon,
  ArrowRightLong,
  Actions as ActionsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Book,
  MapPin,
  Calendar,
  DocumentModal,
  User as UserIcon,
  Category as CategoryIcon,
  Building,
} from "@/components/icons";
import { InternStatus, type Intern, type InternDetail, type InternType } from "../types/intern-management.types";
import { internManagementApi } from "../services/intern-management.api";
import styles from "./InternDetailDrawer.module.scss";

function formatDateDisplay(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

const INTERN_TYPE_LABELS: Record<InternType, string> = {
  universiteZorunlu: i18n.t("internDetail.types.universityMandatory"),
  liseBeceri: i18n.t("internDetail.types.highSchoolSkill"),
};

const STATUS_LABELS: Record<InternStatus, string> = {
  [InternStatus.AKTIF]: "Aktif",
  [InternStatus.TAMAMLANDI]: i18n.t("internDetail.completed"),
};

interface InternDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  intern: Intern | null;
  onGoToDetail?: (intern: Intern) => void;
  onEdit?: (intern: Intern) => void;
  onDelete?: (intern: Intern) => void;
}

const TOTAL_TRAININGS = 20;

const InternDetailDrawer: React.FC<InternDetailDrawerProps> = ({
  isOpen,
  onClose,
  intern,
  onGoToDetail,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [detail, setDetail] = useState<InternDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && intern) {
      setDetailLoading(true);
      setDetail(null);
      internManagementApi
        .getById(intern.id)
        .then((res) => setDetail(res.data))
        .catch(() => setDetail(null))
        .finally(() => setDetailLoading(false));
    } else {
      setDetail(null);
    }
  }, [isOpen, intern?.id]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  if (!intern) return null;

  const progressPercent = intern.educationProgress ?? 0;
  const completedCount = Math.round((progressPercent / 100) * TOTAL_TRAININGS);
  const startDateFormatted: string = detail?.startDate ? formatDateDisplay(detail.startDate) : "—";
  const endDateFormatted: string = detail?.endDate ? formatDateDisplay(detail.endDate) : "—";
  const school: string = detail?.school ?? "—";
  const schoolDepartment: string = detail?.schoolDepartment ?? "—";
  const grade: string = detail?.grade ?? "—";
  const phone: string = detail?.phone ?? "—";
  const email: string = detail?.email ?? "—";
  const supervisorName: string = detail?.supervisorName ?? "—";

  const headerTitle = (
    <span className={styles.headerTitle}>
      <EyeIcon width={24} height={24} color="#14181F" />
      {t("internManagement.detailDrawer.title")}
    </span>
  );

  const headerActions = (
    <div className={styles.headerActions}>
      <Button
        type="button"
        variant="text"
        color="secondary"
        endIcon={<ArrowRightLong width={16} height={16} color="#707A8F" />}
        className={styles.detayButton}
        onClick={() => {
          onGoToDetail?.(intern);
          onClose();
        }}
      >
        {t("internManagement.detailDrawer.goToDetail")}
      </Button>
      <div className={styles.menuWrapper} ref={menuRef}>
        <button
          type="button"
          className={styles.menuButton}
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-haspopup="true"
          aria-expanded={menuOpen}
        >
          <ActionsIcon width={20} height={20} />
        </button>
        {menuOpen && (
          <div className={styles.menuDropdown}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                setMenuOpen(false);
                onEdit?.(intern);
                onClose();
              }}
            >
              <EditIcon width={18} height={18} />
              {t("internManagement.actions.edit")}
            </button>
            {onDelete && (
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => {
                  setMenuOpen(false);
                  onDelete(intern);
                  onClose();
                }}
              >
                <DeleteIcon width={18} height={18} />
                {t("internManagement.actions.delete")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={headerTitle}
      headerActions={headerActions}
      width="561px"
      position="right"
    >
      <div className={styles.drawerContent}>
        {detailLoading ? (
          <div className={styles.loadingState}>
            {t("common.loading")}
          </div>
        ) : (
          <>
            <div className={styles.personnelCard}>
              <div className={styles.personnelCardTop}>
                <div className={styles.avatarWrap}>
                  <Avatar alt={intern.name} color="primary" />
                </div>
                <div className={styles.personnelInfo}>
                  <div className={styles.personnelHeader}>
                    <div className={styles.personnelNameTitle}>
                      <h2 className={styles.personnelName}>{intern.name}</h2>
                      <p className={styles.personnelTitle}>
                        {t("internManagement.detailDrawer.role")}
                      </p>
                    </div>
                    <div className={styles.badgesRow}>
                      <Chip type="success" size="small">
                        {STATUS_LABELS[intern.status]}
                      </Chip>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.contactPills}>
                <span className={styles.contactPill}>TC {intern.tc}</span>
                <span className={styles.contactPill}>{phone}</span>
                <span className={styles.contactPill}>{email}</span>
              </div>
              <div className={styles.cardEducationProgress}>
                <h4 className={styles.cardEducationProgressTitle}>
                  {t("internManagement.detailDrawer.educationProgress")}
                </h4>
                <ProgressBar
                  value={progressPercent}
                  score={completedCount}
                  total={TOTAL_TRAININGS}
                  showLabel={false}
                  size="medium"
                />
                <div className={styles.progressLabels}>
                  <span>
                    ({completedCount}/{TOTAL_TRAININGS})
                  </span>
                  <span className={styles.progressPercent}>%{progressPercent}</span>
                </div>
              </div>
            </div>

            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {t("internManagement.detailDrawer.internshipInfo")}
              </h4>
              <div className={styles.sectionCard}>
                <div className={styles.detailList}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Book width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("internManagement.detailDrawer.internType")}
                    </span>
                    <span className={styles.detailValue}>
                      {INTERN_TYPE_LABELS[intern.internType]}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <MapPin width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("internManagement.detailDrawer.internshipLocation")}
                    </span>
                    <span className={styles.detailValue}>{intern.unitOrBusiness}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Calendar width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("internManagement.detailDrawer.internshipDates")}
                    </span>
                    <span className={styles.detailValue}>
                      {startDateFormatted} - {endDateFormatted}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <DocumentModal width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("internManagement.detailDrawer.absenteeism")}
                    </span>
                    <span className={styles.detailValue}>—</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <UserIcon width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("internManagement.detailDrawer.supervisor")}
                    </span>
                    <span className={styles.detailValue}>{supervisorName}</span>
                  </div>
                </div>
              </div>
            </section>

            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {t("internManagement.detailDrawer.personalAcademicInfo")}
              </h4>
              <div className={styles.sectionCard}>
                <div className={styles.detailList}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Book width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("internManagement.detailDrawer.school")}
                    </span>
                    <span className={styles.detailValue}>{school}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <Building width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("internManagement.detailDrawer.department")}
                    </span>
                    <span className={styles.detailValue}>{schoolDepartment}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailIcon}>
                      <CategoryIcon width={20} height={20} />
                    </span>
                    <span className={styles.detailLabel}>
                      {t("internManagement.detailDrawer.class")}
                    </span>
                    <span className={styles.detailValue}>{grade}</span>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default InternDetailDrawer;
